import type { Observable } from "@legendapp/state";
import { For, useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { Link, useNavigate } from "@tanstack/react-router";

import { OrgAvatar } from "~/components/org-avatar";
import { appDataReady$ } from "~/stores/app-data-ready";
import { organizationInvitations$ } from "~/stores/organization-invitations";
import {
  activeOrganization$,
  type Organization,
  organizations$,
  setActiveOrganization,
} from "~/stores/organizations";

type OrgHeaderProps = {
  collapsed?: boolean;
};

export const OrgHeader = ({ collapsed = false }: OrgHeaderProps) => {
  const navigate = useNavigate();
  const orgId = useValue(activeOrganization$.id);
  const orgLogo = useValue(activeOrganization$.logo);
  const orgName = useValue(activeOrganization$.name);
  const invitations = useValue(organizationInvitations$);
  const pendingInvites = Object.values(invitations ?? {});
  const hasPendingInvites = pendingInvites.length > 0;
  const appDataReady = useValue(appDataReady$);

  if (!orgId) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size={collapsed ? "icon-lg" : "lg"}
              className={collapsed ? "shrink-0" : "shrink grow"}
            />
          }
        >
          <Icon.Logo
            name="mora"
            className={cn("size-5 shrink-0", !collapsed && "-ml-1")}
          />
          {!collapsed && (
            <>
              <span className="min-w-0 truncate text-foreground">Mora</span>
              <span className="-mr-1.5 ml-auto shrink-0">
                <Icon name="chevron-double-up-down" />
              </span>
            </>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-56">
          <DropdownMenuItem
            render={(props) => (
              <Link {...props} to="/logout" preload={false}>
                <Icon name="arrow-right-bracket" />
                Log out
              </Link>
            )}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size={collapsed ? "icon-lg" : "lg"}
            className={collapsed ? "shrink-0" : "shrink grow"}
          />
        }
      >
        <div className={cn("relative shrink-0", !collapsed && "-ml-1")}>
          <OrgAvatar name={orgName} logo={orgLogo} className="size-5" />
          {hasPendingInvites && (
            <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-accent-foreground" />
          )}
        </div>
        {!collapsed && (
          <>
            <span className="min-w-0 truncate text-foreground">{orgName}</span>
            <span className="-mr-1.5 ml-auto shrink-0">
              <Icon name="chevron-double-up-down" />
            </span>
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56">
        <DropdownMenuItem
          disabled={!appDataReady}
          render={(props) => (
            <Link {...props} to="/settings/organization">
              <Icon name="cog" />
              Workspace settings
            </Link>
          )}
        />
        <DropdownMenuItem
          disabled={!appDataReady}
          render={(props) => (
            <Link {...props} to="/settings/members">
              <Icon name="person-plus" />
              Invite members
            </Link>
          )}
        />
        <DropdownMenuSeparator />
        {hasPendingInvites && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span className="relative inline-flex items-center gap-2">
                <span className="size-2 shrink-0 rounded-full bg-accent-foreground" />
                Join
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent sideOffset={8} alignOffset={-4}>
              {pendingInvites.map((invite) => (
                <DropdownMenuItem
                  key={invite.id}
                  onClick={() =>
                    navigate({ to: "/join", search: { id: invite.id } })
                  }
                >
                  <OrgAvatar
                    name={invite.organizationName}
                    logo={invite.organizationLogo}
                    className="size-5"
                  />
                  <span>{invite.organizationName}</span>
                  <span className="ml-auto text-muted-foreground text-xs">
                    {invite.membersCount ?? 0}{" "}
                    {invite.membersCount === 1 ? "member" : "members"}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {!appDataReady && (
          <>
            <DropdownMenuItem
              render={(props) => (
                <Link {...props} to="/logout" preload={false}>
                  <Icon name="arrow-right-bracket" />
                  Log out
                </Link>
              )}
            />
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Switch workspace</DropdownMenuSubTrigger>
          <DropdownMenuSubContent sideOffset={8} alignOffset={-4}>
            <For each={organizations$} item={OrgInviteItem} />
            <DropdownMenuSeparator />
            <DropdownMenuItem
              render={(props) => (
                <Link {...props} to="/create">
                  <Icon name="plus" />
                  Create workspace
                </Link>
              )}
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const OrgInviteItem = ({ item$ }: { item$: Observable<Organization> }) => {
  const id = useValue(item$.id);
  const logo = useValue(item$.logo);
  const name = useValue(item$.name);
  const isActive = useValue(() => id === activeOrganization$.id.get());

  return (
    <DropdownMenuItem
      onClick={() => {
        if (!isActive) {
          setActiveOrganization(id);
        }
      }}
    >
      <OrgAvatar name={name} logo={logo} className="size-4" />
      {name}
      {isActive ? (
        <Icon name="check-circle" className="ml-auto" />
      ) : (
        <div className="ml-auto size-4" />
      )}
    </DropdownMenuItem>
  );
};
